"use client";

import { ILeanProductTile } from "@faire/web-api/indigofair/data/ILeanProductTile";
import { Spacer } from "@faire/web/slate/spacing";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import React from "react";

import { Caseline } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/CaseSizeText/Caseline";
import { productTileCaselinePropsAdaptor } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/CaseSizeText/productTileCaselinePropsAdapter";
import { DebugInfoSection } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/DebugInfoSection/DebugInfoSection";
import { debugInfoSectionPropsILeanProductTileAdapter } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/DebugInfoSection/debugInfoSectionPropsILeanProductTileAdapter";
import { Favorite } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/Favorite/Favorite";
import { productTileFavoritePropsILeanProductTileAdapter } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/Favorite/FavoriteProps";
import { ProductImage } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/ProductImage/ProductTileImage";
import { MaybePriceFirstProductTileWrapper } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/ProductInfo/PriceText/MaybePriceFirstProductTileWrapper";
import {
  ClientFormatMoneyPriceWrapperProps,
  PriceWithClientFormatMoney,
} from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/ProductInfo/PriceText/PriceWithClientFormatMoney";
import { ProductName } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/ProductInfo/ProductName";
import { ProductTileContainer } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/ProductTileContainer/ProductTileContainer";
import { ProductTileOverlay } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/ProductTileOverlay/ProductTileOverlay";
import { productTileOverlayProductTileAdaptor } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/ProductTileOverlay/productTileOverlayUtils";
import { ProductTileTrackingWrapper } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/ProductTileTrackingWrapper/ProductTileTrackingWrapper";
import { productTileTrackingWrapperPropsILeanProductTileAdapter } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/ProductTileTrackingWrapper/ProductTileTrackingWrapperProps";
import { productTileQuickAddPropsILeanProductTileAdapter } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/QuickAdd/QuickAddProps";
import { TopBadges } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/TopBadges/TopBadges";
import { productTileTopBadgesPropsILeanProductTileAdapter } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/TopBadges/TopBadgesProps";
import { ProductTileContainerRefContextProvider } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/ProductTileContainerRefContextProvider";
import { ProductTileContextProvider } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/ProductTileContextProvider";
import { ProductTileSwatches } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/ProductTileSwatches";
import { PLACEHOLDER_IMAGE } from "@faire/retailer/features/MarketplaceItems/ProductDetailsTile/consts";
import { useIsLoggedInRetailerOrBrandPreview } from "@faire/retailer/lib/isLoggedInRetailer";
import { showProductPromotions } from "@faire/retailer/lib/showProductPromotions";
import { generateProductUrlForBrandPage } from "@faire/retailer/views/brand/utils/generateProductUrlForBrandPage";
import { PRICE_TEXT_SURFACE } from "@faire/retailer/views/product/ProductDetails/Price/PriceProps";

import { NotifyMe } from "./__internal__/components/NotifyMe/NotifyMe";
import { notifyMeProductTileAdaptor } from "./__internal__/components/NotifyMe/NotifyMeUtils";
import { ProductTileImageProps } from "./__internal__/components/ProductImage/ProductTileImageProps";
import { ProductLinkForSDUIEvaluateTile } from "./__internal__/components/ProductLink/SDUIEvaluate/ProductLinkForSDUIEvaluateTile";
import { SDUIEvaluateProductTileClickProvider } from "./__internal__/components/ProductTileClickProvider/SDUIEvaluate/SDUIEvaluateProductTileClickProvider";
import { ProductTileUpperContainer } from "./__internal__/components/ProductTileUpperContainer/ProductTileUpperContainer";
import { QuickAdd } from "./__internal__/components/QuickAdd/QuickAdd";

export const SDUIEvaluateProductTile: React.FC<{
  productTile: ILeanProductTile;
  brand: ILeanProductTile.IBrand | undefined;
}> = ({ productTile, brand }) => {
  const { isDesktopAndAbove } = useViewport();
  const token = productTile.product?.token;

  const trackingWrapperProps =
    productTileTrackingWrapperPropsILeanProductTileAdapter(productTile);
  const productTileDebugInfoProps =
    debugInfoSectionPropsILeanProductTileAdapter(productTile);

  const isLoggedInOrBrandPreview = useIsLoggedInRetailerOrBrandPreview();

  const productTileImageProps: ProductTileImageProps = {
    image: productTile.best_image ?? PLACEHOLDER_IMAGE,
    showVerticalImagery: false,
    imageAltText: "",
    shouldUseLazyImageLoading: false,
    //Client prop
    onLoad: () => {},
    disableRedirectToPdp: true,
  };
  const productTileOverlayProps = productTileOverlayProductTileAdaptor(
    productTile,
    true
  );
  const topBadgeProps =
    productTileTopBadgesPropsILeanProductTileAdapter(productTile);

  const brandPriceTextProps: ClientFormatMoneyPriceWrapperProps = {
    min_option_wholesale_price: productTile.min_option_wholesale_price,
    min_option_wholesale_promo_price:
      productTile.min_option_wholesale_promo_price,
    min_option_retail_price: productTile.min_option_retail_price,
    showProductPromotions: showProductPromotions(
      !productTile.is_locked_view
        ? productTile.min_option_wholesale_promo_price
        : undefined
    ),
    small: true,
    isLoggedInOrBrandPreview,
    isTilePricing: true,
    priceTextSurface: PRICE_TEXT_SURFACE.BRAND,
  };

  const caseLineProps = productTileCaselinePropsAdaptor({
    caseSizeText: productTile.case_size_text,
    optionAvailableUnit:
      productTile.quick_add?.quick_add_option?.option_available_units,
    optionUnitMultiplier:
      productTile.quick_add?.quick_add_option?.option_unit_multiplier,
    productState: productTile.product?.state,
    isLoggedIn: true,
  });
  const quickAddProps =
    productTileQuickAddPropsILeanProductTileAdapter(productTile);
  const favoriteProps =
    productTileFavoritePropsILeanProductTileAdapter(productTile);

  const selectedProductOptionToken = productTile?.selected_product_option_token;
  const productUrl = generateProductUrlForBrandPage(
    productTile.product?.token ?? "",
    selectedProductOptionToken
  );

  if (!token) {
    return null;
  }

  return (
    <ProductTileTrackingWrapper data={trackingWrapperProps}>
      <ProductTileContextProvider brandToken={brand?.token} brand={brand}>
        <SDUIEvaluateProductTileClickProvider
          productUrl={productUrl}
          productTile={productTile}
        >
          <ProductTileContainer>
            <ProductLinkForSDUIEvaluateTile productToken={token}>
              <ProductTileContainerRefContextProvider>
                <ProductTileUpperContainer>
                  <ProductImage data={productTileImageProps} />
                  <ProductTileOverlay data={productTileOverlayProps} />
                  <TopBadges data={topBadgeProps} />
                  <DebugInfoSection data={productTileDebugInfoProps} />
                  <NotifyMe data={notifyMeProductTileAdaptor(productTile)} />
                  <QuickAdd
                    showOnHover={isDesktopAndAbove}
                    data={quickAddProps}
                  />
                  <Favorite data={favoriteProps} />
                </ProductTileUpperContainer>
              </ProductTileContainerRefContextProvider>
              <Spacer size={{ mobileAndAbove: "1x", tabletAndAbove: 12 }} />
              <MaybePriceFirstProductTileWrapper isPriceFirstSlot={true}>
                <PriceWithClientFormatMoney {...brandPriceTextProps} />
                <Spacer size="1/2x" />
              </MaybePriceFirstProductTileWrapper>
              <ProductName productName={productTile?.product?.name ?? ""} />
              <MaybePriceFirstProductTileWrapper isPriceFirstSlot={false}>
                <PriceWithClientFormatMoney {...brandPriceTextProps} />
              </MaybePriceFirstProductTileWrapper>
              <ProductTileSwatches
                optionDetails={productTile.options_details}
              />
              <Caseline {...caseLineProps} />
            </ProductLinkForSDUIEvaluateTile>
          </ProductTileContainer>
        </SDUIEvaluateProductTileClickProvider>
      </ProductTileContextProvider>
    </ProductTileTrackingWrapper>
  );
};
